import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import EmptyButton from 'ls-common-client/dist/components/EmptyButton';
import Text from 'ls-common-client/dist/components/Text';
import LayoutContainer from '../../components/LayoutContainer';
import profileUrl from '../../utils/profileUrl';
import useApi from '../../hooks/useApi';

const StyledRoot = styled(Container)`
  width: 100%;
`;

const StyledWrapper = styled(Container)`
  margin-top: 30px;
  margin-bottom: 30px;
  width: 100%;
  background: #ffffff;
  border-radius: 11px;
  box-shadow: 0 1px 20px 0 rgba(0, 0, 0, 0.15);
  border: solid 1px #ececec;
  padding: 25px;
`;

const StyledList = styled(Container)`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;

const StyledLink = styled(Link)`
  color: ${props => props.theme.text.normal};
  font-size: 16px;
  min-width: 33.33%;
  max-width: 33.33%;
  flex: 1;
  display: block;
  padding-right: 30px;
  min-height: 35px;
  line-height: 20px;
  padding-bottom: 10px;
  padding-top: 4px;
  &:hover {
    color: ${props => props.theme.primary.normal};
  }

  @media ${props => props.theme.mediaQueries.tablet} {
    min-width: 50%;
    max-width: 50%;
  }
  @media ${props => props.theme.mediaQueries.mobile} {
    min-width: 100%;
    max-width: 100%;
  }
`;

const StyledHeading = styled(Container)`
  font-size: 32px;
  line-height: 1.2;
  margin-bottom: 3px;
  font-weight: 600;
  @media ${props => props.theme.mediaQueries.mobile} {
    font-size: 24px;
    margin-top: 30px;
  }
`;

const StyledSubHeading = styled(Container)`
  font-size: 20px;
  line-height: 1.3;
  margin-bottom: 15px;

  @media ${props => props.theme.mediaQueries.mobile} {
    font-size: 16px;
  }
`;

const StyledNav = styled(Container)`
  display: flex;
  width: 100%;
  justify-content: center;
`;

const StyledPagination = styled(Container)`
  flex: 1;
  width: 30px;
  max-width: 30px;
`;

const StyledButton = styled(EmptyButton)`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 16px;
  span[class*='icon-'] {
    display: block;
    padding-top: 4px;
  }
  &.Suburb__BusinessList__active,
  &:not(:disabled):hover {
    color: ${props => props.theme.primary.normal};
    .Suburb__BusinessList__number {
      border: 1px solid ${props => props.theme.primary.normal};
    }
  }
`;

const StyledPageNumber = styled(Text)`
  display: block;
  width: 27px;
  height: 27px;
  border-radius: 100%;
  border: 1px solid transparent;
  min-width: 27px;
`;

const BusinessList = ({ suburb, state }) => {
  const [list, setList] = useState();
  const [isLastPage, setLastPage] = useState();
  const [page, setPage] = useState(1);

  const { data, fetch, loading } = useApi('SeoSuburbListings', {
    params: { suburb, state, page: page - 1 },
  });

  useEffect(() => {
    if (!data && !list && !loading && suburb) {
      fetch();
    }
  }, [data, fetch, list, loading, suburb]);

  useEffect(() => {
    if (data && data.suburbListings) {
      const { suburbListings, last_page: lastPage } = data;
      setList(suburbListings);
      setLastPage(lastPage);
    }
  }, [data]);

  const fetchData = useCallback(
    value => {
      setPage(value + 1);
      fetch({
        params: { suburb, state, page: value },
      });
    },
    [fetch, state, suburb]
  );

  const pages = useMemo(() => {
    const pagination = [
      {
        current: true,
        fetchPage: page - 1,
        page,
      },
    ];
    if (page > 1) {
      pagination.unshift({
        fetchPage: page - 2,
        page: page - 1,
      });
      pagination.unshift({
        fetchPage: page - 2,
        prev: page > 1,
      });
    }
    if (!isLastPage && !loading) {
      pagination.push({
        fetchPage: page,
        page: page + 1,
      });
      pagination.push({
        fetchPage: page,
        next: !isLastPage,
      });
    }
    return pagination;
  }, [isLastPage, page, loading]);

  if (!list || (list && !list.length)) return '';

  return (
    <LayoutContainer>
      <StyledRoot className="Suburb__BusinessList__root">
        <StyledHeading className="Suburb__BusinessList__heading">
          Businesses in {suburb}
        </StyledHeading>
        <StyledSubHeading className="Suburb__BusinessList__subHeading">
          A complete overview of all businesses in {suburb}
        </StyledSubHeading>
        <StyledWrapper className="Suburb__BusinessList__wrapper">
          <StyledList className="Suburb__BusinessList__list">
            {/* FIXME NEED TO FETCH FROM PROFILE SEARCH */}
            {list.map(({ name, hash }) => (
              <StyledLink
                key={hash}
                className="Suburb__BusinessList__link"
                to={profileUrl({
                  name,
                  hash,
                  suburb,
                  state,
                })}
              >
                {name} in {suburb}
              </StyledLink>
            ))}
          </StyledList>
          {!(page === 1 && isLastPage) && (
            <StyledNav className="Suburb__BusinessList__nav">
              {pages.map(item => (
                <StyledPagination
                  key={`${item.page}${item.fetchPage}`}
                  className="Suburb__BusinessList__page"
                  data-page={item.fetchPage}
                  data-displaypage={item.page}
                >
                  <StyledButton
                    className={`Suburb__BusinessList__button ${item.current &&
                      'Suburb__BusinessList__active'}`}
                    onClick={() => {
                      fetchData(item.fetchPage);
                    }}
                  >
                    {item.prev && (
                      <span className="icon-generalcaretleftmedium" />
                    )}
                    {item.page && (
                      <StyledPageNumber className="Suburb__BusinessList__number">
                        {item.page}
                      </StyledPageNumber>
                    )}
                    {item.next && (
                      <span className="icon-generalcaretrightmedium" />
                    )}
                  </StyledButton>
                </StyledPagination>
              ))}
            </StyledNav>
          )}
        </StyledWrapper>
      </StyledRoot>
    </LayoutContainer>
  );
};

export default BusinessList;
